import style from "@/styles/pages/CoApplicant/CoApplicant.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCoapplicantComplete } from "@/slices/progressSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { selectApplication } from "@/slices/applicationSlice";
import { addCoApplicant, coapplicant, selectCoApplicant, setCoapplicantInfo } from "@/slices/coApplicantSlice";
import FadeTransition from "@/styles/animation/FadeTransition";
import { RootState } from "@/store/dtm";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createApiClient } from "@/lib/Requests";
import { CoApplicantForMortgageDto, CoApplicantForMortgageManipulationDto } from "@/lib/DTMWebAPIClient.generated";
import DateComponent from "@/components/shared/DateComponent";
import { setClickInsightFormErrors, validateAge } from "@/lib/util";
import dayjs from "dayjs";
import { Toast } from "primereact/toast";
import InputMask from "react-input-mask";

export default function AddCoApplicant() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const application = useSelector(selectApplication).application;
    const applicant = useSelector((state: RootState) => state.applicant.applicant);
    const coapplicants = useSelector(selectCoApplicant).coapplicants;
    const [dobYear, setDobYear] = useState("");
    const [dobMonth, setDobMonth] = useState("");
    const [dobDay, setDobDay] = useState("");
    const [ageErrorState, setAgeErrorState] = useState({ hasError: false, errorMessage: "", invalidYear: false, invalidDay: false })
    const [editMode, setEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [allowSpouse, setAllowSpouse] = useState(true);
    const errorToast = useRef<Toast>(null);

    const methods = useForm({
        defaultValues: {
            relationship: "",
            firstName: "",
            lastName: "",
            year: "",
            month: "",
            day: "",
            phoneNumber: "",
            emailAddress: "",
            inviteCode: "",
            applicantId: 0,
        },
    });
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    useEffect(() => {
        document.title = "Enter co-applicant details.";

        //dispatch(setCoapplicantComplete(true));
        //setClickInsightEvents(application);

        if (state != null) {
            setEditMode(true);
            let coapplicant = coapplicants.find((c) => c.id == state.id);
            methods.setValue("relationship", coapplicant ? coapplicant.relationship : "");
            methods.setValue("firstName", coapplicant ? coapplicant.firstName : "");
            methods.setValue("lastName", coapplicant ? coapplicant.lastName : "");
            methods.setValue("year", coapplicant ? coapplicant.dob_year : "");
            methods.setValue("month", coapplicant ? coapplicant.dob_month : "");
            methods.setValue("day", coapplicant ? coapplicant.dob_day : "");
            methods.setValue("phoneNumber", coapplicant ? coapplicant.phoneNumber : "");
            methods.setValue("emailAddress", coapplicant ? coapplicant.email : "");
            methods.setValue("inviteCode", coapplicant ? coapplicant.inviteCode : "");
            methods.setValue("applicantId", coapplicant ? coapplicant.applicantId : 0);
        }

        coapplicants.forEach(c => {
            if (c.relationship == "Spouse") {
                setAllowSpouse(false);
            }
        });
    }, []);

    const setYearHandler = (e: any) => {
        setDobYear(e.target.value)
    };
    const setMonthHandler = (e: any) => {
        setDobMonth(e.target.value)
    };
    const setDayHandler = (e: any) => {
        setDobDay(e.target.value)
    };

    const validateDay = () => {
        if (ageErrorState.invalidDay) {
            return ageErrorState.errorMessage;
        } else {
            return true;
        }
    }

    const validateYear = () => {
        if (ageErrorState.invalidYear) {
            return ageErrorState.errorMessage;
        } else {
            return true;
        }
    }

    useEffect(() => {
        let dob = dobYear + "/" + dobMonth + "/" + dobDay; //Date of birth in yyyy/mm/dd format
        let validationErrors = validateAge(dob);

        if (!validationErrors.hasError) {
            methods.clearErrors('year')
            methods.clearErrors('month')
            methods.clearErrors('day')
        }

        setAgeErrorState(validationErrors)
        validateDay();
        validateYear();
    }, [dobYear, dobMonth, dobDay]);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = async (data: any) => {
        //Ensure email address is unqiue
        setDisplayError(false);
        if (data.emailAddress == credential.credential.email) {
            methods.setError("emailAddress", { type: "custom", message: "Please enter a different email. This one is already used for your profile." });
            return;
        }
        if (applicant.email == data.emailAddress) {
            methods.setError("emailAddress", { type: "custom", message: "Please enter a different email. This one is already used by one of your co-applicants." });
            return;
        }
        let duplicateExists = false;
        coapplicants.forEach(c => {
            if (c.email == data.emailAddress && !editMode) {
                duplicateExists = true;
            }
        });
        if (duplicateExists) {
            methods.setError("emailAddress", { type: "custom", message: "Please enter a different email. This one is already used by one of your co-applicants." });
            return;
        }
        console.log(data);
        setIsSaving(true);
        if (editMode) {
            await client.apiCoapplicantsUpdate(new CoApplicantForMortgageManipulationDto({
                code: data.inviteCode,
                isActiveLink: true,
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
                phoneNumber: data.phoneNumber,
                emailAddress: data.emailAddress,
                relationship: data.relationship,
                mainApplicantName: application.id != 0 ? applicant.firstName + " " + applicant.lastName : credential.credential.email,
            })).then((result) => {

                let coapplicantInfo: coapplicant = {
                    id: editMode ? state.id : (coapplicants.length + 1).toString(),
                    applicantId: data.applicantId,
                    relationship: data.relationship,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    dob_year: data.year,
                    dob_month: data.month,
                    dob_day: data.day,
                    phoneNumber: data.phoneNumber,
                    email: data.emailAddress,
                    inviteCode: data.inviteCode,
                    isInviteActive: true
                }
                dispatch(setCoapplicantInfo(coapplicantInfo));
                navigate("/single/co-applicant", { replace: true });

            }).catch((error) => {
                setIsSaving(false);
                console.log("Error saving co-applicant", error);
                if (error.response && error.response.code == 1002) {
                    setDisplayError(true);
                }
            });
        } else {
            await client.apiCoapplicantsAdd(new CoApplicantForMortgageDto({
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: dayjs(data.year + "-" + data.month + "-" + data.day),
                phoneNumber: data.phoneNumber,
                emailAddress: data.emailAddress,
                relationship: data.relationship,
                mainApplicantName: application.id != 0 ? applicant.firstName + " " + applicant.lastName : credential.credential.email,
            })).then((result) => {
                if (result) {
                    let coapplicantInfo: coapplicant = {
                        id: editMode ? state.id : (coapplicants.length + 1).toString(),
                        applicantId: result.applicantId ? result.applicantId : 0,
                        relationship: result.relationship ? result.relationship : "",
                        firstName: result.firstName ? result.firstName : "",
                        lastName: result.lastName ? result.lastName : "",
                        dob_year: data.year,
                        dob_month: data.month,
                        dob_day: data.day,
                        phoneNumber: result.phoneNumber ? result.phoneNumber : "",
                        email: result.emailAddress ? result.emailAddress : "",
                        inviteCode: result.inviteCode ? result.inviteCode : "",
                        isInviteActive: result.isActiveLink != undefined ? result.isActiveLink : true
                    }
                    dispatch(addCoApplicant(coapplicantInfo));
                    navigate("/single/co-applicant", { replace: true });
                }

            }).catch((error) => {
                setIsSaving(false);
                console.log("Error creating co-applicant", error);
                errorToast.current?.show({ severity: 'error', summary: "There was a problem adding a co-applicant.", life: 5000 });
                if (error.response && error.response.code == 1002) {
                    setDisplayError(true);
                }
            });
        }
    };

    return (
        <>
            <FadeTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <Toast ref={errorToast} />
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Co-Applicant Header"
                    >
                        <h1 aria-label="Address Header">Tell us about your co-applicant</h1>
                        <p className="sub-heading">Please provide their contact details and relationship to you. We will send them an email invitation once you submit this application.</p>
                        <p className="mt-5"><b>Note:</b> We will not process this application until your co-applicant(s) provide consent.</p>

                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    This email address is used for another application; please use an alternative email address.
                                </span>
                            </div>
                        }
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="col-span-1 mb-5 mt-5">
                                        <label htmlFor="applicantRelationship">What is this applicant's relationship to you?</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        methods.formState?.errors?.relationship ? "" : "hidden",
                                                        "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <select
                                                id="applicantRelationship"
                                                {...methods.register("relationship", {
                                                    required: "Select a relationship."
                                                })}
                                                defaultValue=""
                                                style={{ maxWidth: 'none' }}
                                                className={clsx(
                                                    methods.formState?.errors?.relationship?.message ? "error" : "",
                                                    "mt-2 pt-2.5"
                                                )}
                                            >
                                                <option hidden value="">
                                                    Choose...
                                                </option>
                                                {(allowSpouse || methods.getValues("relationship") == "Spouse") && <option value="Spouse">Spouse</option>}
                                                <option value="ParentLegalGuardian">Parent/Legal Guardian</option>
                                                <option value="OtherFamilyMember">Other Family Member</option>
                                                <option value="Friend">Friend</option>
                                                <option value="Child">Child 18+</option>
                                            </select>
                                        </div>
                                        {methods.formState?.errors?.relationship?.message && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {methods.formState.errors.relationship.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex md:flex-row flex-col w-max mt-5">
                                        <div>
                                            <label htmlFor="phoneNumber">Legal First Name</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.firstName
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        "mt-2 w:[220px] md:w-[222px]", style.livableArea
                                                    )}
                                                    {...methods.register("firstName", {
                                                        required: "Enter their first name.",
                                                        maxLength: {
                                                            value: 38,
                                                            message: "Name exceeds maximum character count",
                                                        },
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9 '-.]*$/,
                                                            message: "Please enter valid first name",
                                                        },
                                                    })}
                                                    type="text"
                                                    min={1}
                                                    placeholder=""
                                                    id="livableArea"
                                                />
                                            </div>
                                            {methods.formState?.errors?.firstName?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.firstName.message}
                                                </span>
                                            )}
                                        </div>
                                        <div className="md:ml-4">
                                            <label htmlFor="phoneNumber">Legal Last Name</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.lastName
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        "mt-2 w:[220px] md:w-[222px]", style.livableArea
                                                    )}
                                                    {...methods.register("lastName", {
                                                        required: "Enter their last name.",
                                                        maxLength: {
                                                            value: 38,
                                                            message: "Name exceeds maximum character count",
                                                        },
                                                        pattern: {
                                                            value: /^[a-zA-Z0-9 '-.]*$/,
                                                            message: "Please enter valid last name",
                                                        },
                                                    })}
                                                    type="text"
                                                    min={1}
                                                    placeholder=""
                                                    id="lastName"
                                                />
                                            </div>
                                            {methods.formState?.errors?.lastName?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.lastName.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="dobMonth">Date of birth
                                            <DateComponent readOnly={false} callbackYear={setYearHandler} callbackMonth={setMonthHandler} callbackDay={setDayHandler} validateDay={validateDay} validateYear={validateYear} />
                                        </label>
                                    </div>
                                    <div className="mt-5">
                                        <div className="mt-5">
                                            <label htmlFor="phoneNumber">Phone number</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.phoneNumber
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <InputMask
                                                    type="tel"
                                                    placeholder="(000) 000-0000"
                                                    alwaysShowMask={false}
                                                    maskChar={null}
                                                    mask="(999) 999-9999"
                                                    className={clsx(
                                                        methods.formState?.errors?.phoneNumber?.message
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[320px] md:w-[322px]"
                                                    )}
                                                    {...methods.register("phoneNumber", {
                                                        required: "Enter a valid phone number",
                                                        pattern: {
                                                            value:
                                                                // eslint-disable-next-line no-useless-escape
                                                                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                                                            message: "Enter a valid phone number",
                                                        },
                                                    })}
                                                />
                                            </div>
                                            {methods.formState?.errors?.phoneNumber?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    Enter a valid phone number
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="phoneNumber">Email Address</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        methods.formState?.errors?.emailAddress
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <input
                                                className={clsx(
                                                    "mt-2 w:[320px] md:w-[322px]", style.livableArea
                                                )}
                                                {...methods.register("emailAddress", {
                                                    required: "Enter the email address of this co-applicant.",
                                                })}
                                                type="email"
                                                min={1}
                                                placeholder=""
                                                id="emailAddress"
                                            />
                                        </div>
                                        {methods.formState?.errors?.emailAddress?.message && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {methods.formState.errors.emailAddress.message}
                                            </span>
                                        )}
                                        {!editMode && <p style={{ fontSize: 12 }}>example@domain.com</p>}
                                    </div>
                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                style={{ textWrap: "nowrap", width: 150 }}
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode && "Save Changes"}
                                                {!editMode && "Add co-applicant"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode && "Save Changes"}
                                                {!editMode && "Add co-applicant"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </FadeTransition>
        </>
    );
}